import { inject, Injectable } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';

import { combineLatest, EMPTY, Observable, of, race } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap
} from 'rxjs/operators';

import {
  CustomQuestion,
  PropertyMatchBean,
  PropertySearcherType
} from '@ui/shared/model';
import {
  errorMessageParser,
  getConstants,
  Go,
  isValueNullOrUndefined,
  LocalStorageService,
  ModalService,
  PropertyInterestComponent,
  SessionStorageService,
  ShowError,
  ShowInfo
} from '@ui/legacy-lib';

import { ActivatedRoute } from '@angular/router';

import { ImmomioModalService } from '@immomio/ui/modal';
import { SmsVerificationPromptModalComponent } from '@ui/tenant-pool/shared/sms-verification/feature';
import { SmsModalPropertyHeader } from '@ui/tenant-pool/shared/sms-verification/model';

import { RentedPropertyMatchesStore } from '@ui/tenant-pool/shared/property-matches/data-access';
import { SearchProfileStore } from '@ui/tenant-pool/shared/search-profile/data-access';
import { isCaptchaResponseInvalidError } from '@ui/tenant-pool/shared/util';
import { ApplyIntentStorageService } from '@ui/tenant-pool/shared/expose/apply/util';
import { ExposeFacade } from '@ui/tenant-pool/shared/expose/data-access';
import { storageKeys } from '@ui/tenant-pool/shared/config/util';
import { UserStore } from '@ui/tenant-pool/shared/user/data-access';
import { OpenCustomQuestionsModal } from '../custom-questions/custom-questions.actions';
import {
  LoadSelfDisclosureData,
  OpenSelfDisclosuresModal
} from '../self-disclosure/self-disclosure.actions';
import { ApplicationFacade } from '../../core/services/application.facade';
import { PropertyMatchesFacade } from '../../core/services/property-matches.facade';
import { MainPageNavigation } from '../../config/navigation.config';
import { notificationConfig } from '../../config/notification.config';
import { TenantAcceptanceModalComponent } from '../../components/tenant-acceptance-modal/tenant-acceptance-modal.component';
import { TenantExperienceModalComponent } from '../../components/tenant-experience-modal/tenant-experience-modal.component';
import {
  getPropertyMatchDataActionState,
  getPropertyMatchesData
} from './property-matches.selector';
import {
  CacheIntent,
  CheckApplicationExist,
  CheckApplicationExistFail,
  CheckApplicationExistSuccess,
  CreateApplication,
  CreateApplicationFail,
  CreateApplicationSuccess,
  DeclareIntent,
  DeclareIntentFail,
  DeclareIntentSuccess,
  LoadPropertyMatchBean,
  LoadPropertyMatchBeanFail,
  LoadPropertyMatchBeanQuestions,
  LoadPropertyMatchBeanQuestionsFail,
  LoadPropertyMatchBeanQuestionsSuccess,
  LoadPropertyMatchBeanSuccess,
  LoadPropertyMatchesBeans,
  LoadPropertyMatchesBeansFail,
  LoadPropertyMatchesBeansSuccess,
  LoadRentedFlatsApplications,
  LoadRentedFlatsApplicationsFail,
  LoadRentedFlatsApplicationsSuccess,
  OpenCompleteGarageModal,
  OpenIntentModal,
  OpenPostLoginModal,
  PromptSmsVerificationForApply,
  RevokeAllOpenApplications,
  RevokeAllOpenApplicationsFail,
  RevokeAllOpenApplicationsSuccess,
  UncacheIntent
} from './property-matches.actions';

@Injectable()
export class PropertyMatchesEffects {
  private actions$ = inject(Actions);
  private route = inject(ActivatedRoute);
  private facade = inject(PropertyMatchesFacade);
  private applicationFacade = inject(ApplicationFacade);
  private store = inject(Store);
  private readonly userStore = inject(UserStore);
  private sessionStorage = inject(SessionStorageService);
  private localStorage = inject(LocalStorageService);
  private modalService = inject(ModalService);
  private localStorageService = inject(LocalStorageService);
  private immomioModalService = inject(ImmomioModalService);
  private searchProfileStore = inject(SearchProfileStore);
  private rentedStore = inject(RentedPropertyMatchesStore);
  private exposeFacade = inject(ExposeFacade);
  private applyIntentStorageService = inject(ApplyIntentStorageService);

  checkApplicationExistSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckApplicationExistSuccess),
      mergeMap(({ id, smsVerificationRequired }) => {
        if (id) return [LoadPropertyMatchBean({ id })];
        const captchaResponse = this.sessionStorage.getItem<string>(
          storageKeys.captchaResponseForApply
        );
        return [
          CreateApplication({
            captchaResponse,
            smsVerificationRequired
          })
        ];
      })
    )
  );
  loadPropertyMatchesBeans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPropertyMatchesBeans),
      switchMap(({ data, loadMore }) =>
        this.facade.loadPropertyMatchesBeans(data).pipe(
          map(result => LoadPropertyMatchesBeansSuccess({ result, loadMore })),
          catchError(error => [
            LoadPropertyMatchesBeansFail({ error, loadMore })
          ])
        )
      )
    )
  );
  loadPropertyMatchBean$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPropertyMatchBean),
      switchMap(({ id }) =>
        this.facade.loadPropertyMatchBean(id).pipe(
          map(result => LoadPropertyMatchBeanSuccess({ result })),
          catchError((error: unknown) => {
            return [
              LoadPropertyMatchBeanFail({ error: error as Error }),
              new Go({
                path: [
                  MainPageNavigation.PROPERTIES,
                  MainPageNavigation.PROPERTY_NOT_FOUND
                ]
              })
            ];
          })
        )
      )
    )
  );

  promptSmsVerificationForApply$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PromptSmsVerificationForApply),
        exhaustMap(({ applicationId, customerId, propertyHeader }) =>
          (propertyHeader
            ? of(propertyHeader)
            : this.loadSmsModalPropertyHeader(applicationId)
          ).pipe(
            tap(header => {
              this.immomioModalService.open(
                SmsVerificationPromptModalComponent,
                {
                  data: {
                    prefilledPhone:
                      this.userStore.userData()?.profile?.phone ?? null,
                    propertyName: header?.propertyName ?? null,
                    propertyAddress: header?.propertyAddress ?? null,
                    propertyImageUrl: header?.propertyImageUrl ?? null,
                    customerId: customerId ?? null
                  }
                }
              );
            })
          )
        )
      ),
    { dispatch: false }
  );

  /**
   * Resolves the modal header summary from already-loaded store data.
   *
   * Every origin, appointment booking included, reads the loaded match list.
   * The booking gate used to read the retired `+state/appointments` bundle,
   * which carried the same name/address/image for the same application.
   *
   * Find the clicked application in the loaded list. The id may be the match id
   * or the property id depending on the entry point, so match either. The list
   * query already carries name/address/attachments.
   */
  private loadSmsModalPropertyHeader(
    applicationId: string | undefined
  ): Observable<SmsModalPropertyHeader | null> {
    if (!applicationId) return of(null);

    return this.store.select(getPropertyMatchesData).pipe(
      take(1),
      map(matches => {
        const match = matches?.find(
          m => m.id === applicationId || m.property?.id === applicationId
        );
        const data = match?.property?.data;
        if (!data?.name) return null;
        return {
          propertyName: data.name,
          propertyAddress: data.address ?? null,
          propertyImageUrl: data.attachments?.[0]?.url ?? null
        };
      })
    );
  }

  loadPropertyMatchBeanQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPropertyMatchBeanQuestions),
      switchMap(({ id }) =>
        this.facade.loadPropertyMatchBeanQuestions(id).pipe(
          map(res =>
            LoadPropertyMatchBeanQuestionsSuccess({
              id,
              questionContainer: res.data.customQuestionsByApplication
            })
          ),
          catchError(error => [LoadPropertyMatchBeanQuestionsFail({ error })])
        )
      )
    )
  );
  loadRentedFlatsApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadRentedFlatsApplications),
      switchMap(() => this.applicationFacade.loadRentedFlatsApplications()),
      map(applications => LoadRentedFlatsApplicationsSuccess({ applications })),
      catchError(error => [LoadRentedFlatsApplicationsFail(error)])
    )
  );
  declareIntent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeclareIntent),
      switchMap(({ declareIntentData }) =>
        this.applicationFacade.declareIntent(declareIntentData).pipe(
          mergeMap(() => [
            DeclareIntentSuccess({
              id: declareIntentData.propertySearcherId,
              intent: declareIntentData.intent
            }),
            UncacheIntent(),
            new ShowInfo(notificationConfig.application.declareIntent.success)
          ]),
          catchError(() => [
            DeclareIntentFail({
              error: new Error(notificationConfig.property.remove.error)
            }),
            new ShowError(notificationConfig.application.declareIntent.error)
          ])
        )
      )
    )
  );
  CheckApplicationExist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckApplicationExist),
      switchMap(({ propertyId, captchaResponse }) =>
        this.applicationFacade.checkApplicationExist(propertyId).pipe(
          map(({ id, smsVerificationRequired }) =>
            CheckApplicationExistSuccess({
              id,
              propertyId,
              captchaResponse,
              smsVerificationRequired
            })
          ),
          catchError(e => [CheckApplicationExistFail(e)])
        )
      )
    )
  );
  RevokeAllOpenApplications = createEffect(() =>
    this.actions$.pipe(
      ofType(RevokeAllOpenApplications),
      switchMap(() =>
        this.applicationFacade.revokeAllOpenApplications().pipe(
          map(() => RevokeAllOpenApplicationsSuccess()),
          catchError(() => [
            RevokeAllOpenApplicationsFail({
              error: new Error(
                notificationConfig.propertyApplyProcessPSBeans.close.error
              )
            })
          ])
        )
      )
    )
  );
  cacheIntent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CacheIntent),
        map(action => action.applicationId),
        tap(applicationId =>
          this.sessionStorage.setItem(
            storageKeys.confirmIntentForApplication,
            applicationId
          )
        )
      ),
    { dispatch: false }
  );
  unCacheIntent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UncacheIntent),
        tap(() =>
          this.sessionStorage.removeItem(
            storageKeys.confirmIntentForApplication
          )
        )
      ),
    { dispatch: false }
  );

  createApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateApplication),
      // exhaustMap, not switchMap: the parked apply intent survives until an
      // application exists, so every navigation until then makes
      // ApplicationCreationGuard re-dispatch CheckApplicationExist ->
      // CreateApplication. Restarting here abandoned the in-flight apply and
      // stacked a second SMS prompt on top of the first one.
      exhaustMap(({ smsVerificationRequired, captchaResponse }) => {
        // The property comes from the parked apply intent, not from a store:
        // this runs on the first authenticated navigation, after a Keycloak
        // redirect has reloaded the page and emptied every store.
        const propertyId =
          this.applyIntentStorageService.getPropertyIdStorage();

        const action = () =>
          this.applicationFacade
            .createApplication(
              this.userStore.userId(),
              propertyId,
              captchaResponse
            )
            .pipe(
              mergeMap(application => {
                // The apply can move the PS out of the internal tenant pool, and
                // createApplicationSuccess$ navigates in the same tick. Refreshing
                // the profile first gives InternalPoolGuard a pending state to wait
                // for, so the redirect is not decided on the pre-apply flag.
                // Called before the success action is returned so the load is
                // already pending by the time createApplicationSuccess$
                // navigates - InternalPoolGuard waits on that.
                this.userStore.loadUser();

                return [CreateApplicationSuccess({ application })];
              }),
              catchError(error => {
                this.sessionStorage.removeItem(
                  storageKeys.captchaResponseForApply
                );
                this.localStorage.removeItem(storageKeys.portalRegistration);

                // Still no toast for a rejected captcha: this page renders no
                // widget, so there is nothing the user could act on.
                if (isCaptchaResponseInvalidError(error)) {
                  return [CreateApplicationFail(error)];
                }

                const parsedError = errorMessageParser(
                  error,
                  notificationConfig.application.create.error
                );

                return [CreateApplicationFail(parsedError)];
              })
            );

        if (!smsVerificationRequired) {
          return action();
        }

        return this.exposeFacade.getPropertyCached(propertyId).pipe(
          catchError(() => of(null)),
          switchMap(property => {
            const modal = this.immomioModalService.open(
              SmsVerificationPromptModalComponent,
              {
                data: {
                  prefilledPhone:
                    this.userStore.userData()?.profile?.phone ?? null,
                  propertyName: property?.data?.name,
                  propertyAddress: property?.data?.address,
                  propertyImageUrl: property?.titleImage?.url,
                  customerId: property?.customer?.id
                }
              }
            );

            // Show the SMS prompt first; apply once it is closed or dismissed
            // (verification is informational — the user can always continue).
            return race(
              outputToObservable(modal.closed),
              outputToObservable(modal.dismissed)
            ).pipe(switchMap(() => action()));
          })
        );
      })
    )
  );

  createApplicationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateApplicationSuccess),
        switchMap(({ application }) => {
          const portalRegistration = this.localStorage.getItem<boolean>(
            storageKeys.portalRegistration
          );
          this.localStorage.removeItem(storageKeys.portalRegistration);
          this.sessionStorage.removeItem(storageKeys.captchaResponseForApply);

          // Don't make an additional redirect, because for portal registrations it's already handled
          // in the action Register in the user.effects.ts
          if (portalRegistration) return EMPTY;

          // The application is only loaded on the "already applied" path, so the
          // freshly created one has to be fetched here for the target page.
          this.store.dispatch(LoadPropertyMatchBean({ id: application.id }));

          // Redirect last: applying can move the PS out of the internal tenant
          // pool and the target route is branched on that, so navigating before
          // the refreshed profile landed sends the user to `/my-landlord` with
          // `:customerIdent` stripped off. LoadUserData was dispatched with this
          // action, so both action states are pending by the time we subscribe.
          return combineLatest([
            this.userStore.userDataActionState$,
            this.store.select(getPropertyMatchDataActionState)
          ]).pipe(
            filter(([user, bean]) => !user.pending && !bean.pending),
            take(1),
            tap(() =>
              this.store.dispatch(
                new Go({
                  path: [
                    MainPageNavigation.LANDLORDS,
                    application.customerIdent,
                    'applications',
                    application.id
                  ],
                  query: {
                    origin: `${MainPageNavigation.LANDLORDS}`
                  }
                })
              )
            )
          );
        })
      ),
    { dispatch: false }
  );

  openCompleteGarageModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenCompleteGarageModal),
        tap(() => {
          this.modalService.openAcknowledgement({
            data: {
              titleMessage: 'parking_sp_confirmation.title_l',
              message: 'parking_sp_confirmation.message_l'
            }
          });
        })
      ),
    { dispatch: false }
  );

  openIntentModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenIntentModal),
        switchMap(({ application, intent }) =>
          this.store.select(getConstants).pipe(
            filter(constants => !!constants),
            map(constants => constants.refusalReasonTypes),
            take(1),
            map(reasons => ({
              reasons,
              profileComplete: this.userStore.profileCompleteness() === 100
            })),
            tap(({ reasons, profileComplete }) => {
              const modalPromise =
                this.modalService.promisifyOpen<PropertyInterestComponent>(
                  PropertyInterestComponent,
                  {
                    data: {
                      propertyName: application?.property?.data?.name,
                      propertyType: application?.property?.type,
                      intent: intent,
                      reasons: reasons,
                      profileComplete: profileComplete,
                      askForViewingAppointmentAttendance:
                        application.askForViewingAppointmentAttendance,
                      editProfile: () => {
                        this.store.dispatch(
                          new Go({
                            path: [MainPageNavigation.PROFILE, 'edit', 'step']
                          })
                        );
                      }
                    }
                  }
                );
              void modalPromise.then(modal => {
                modal.onClose().subscribe(result => {
                  const hasIntent = result.intent;
                  if (!!application.property.selfDisclosureId && hasIntent) {
                    this.store.dispatch(
                      new LoadSelfDisclosureData(application)
                    );
                    this.store.dispatch(new OpenSelfDisclosuresModal(true));
                  } else {
                    // DeclareIntent either if self disclosure is not required or if NO_INTENT

                    const declarePayload = {
                      ...result,
                      propertySearcherId: application.id
                    };
                    this.store.dispatch(
                      DeclareIntent({
                        declareIntentData: declarePayload
                      })
                    );
                  }
                });

                modal.onDismiss().subscribe(result => {
                  if (result) {
                    this.store.dispatch(
                      CacheIntent({ applicationId: application.id })
                    );
                  } else {
                    this.store.dispatch(UncacheIntent());
                  }
                });
              });
            })
          )
        )
      ),
    { dispatch: false }
  );

  openPostLoginModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OpenPostLoginModal),
        map(() => {
          const confirmIntentForApplication = this.sessionStorage.getItem(
            storageKeys.confirmIntentForApplication
          );
          const applicationId = this.localStorageService.getItem(
            storageKeys.customQuestionApplicationId
          );
          this.localStorageService.removeItem(
            storageKeys.customQuestionApplicationId
          );

          if (confirmIntentForApplication) {
            this.store.dispatch(
              LoadPropertyMatchBean({
                id: confirmIntentForApplication
              })
            );
            this.store.dispatch(UncacheIntent());
          }

          return {
            applicationId,
            confirmIntentForApplication
          };
        }),
        switchMap(({ applicationId, confirmIntentForApplication }) =>
          this.store.select(getPropertyMatchDataActionState).pipe(
            filter(state => !state.pending),
            take(1),
            switchMap(() =>
              this.store.select(getPropertyMatchesData).pipe(
                filter(
                  (propertiesData: PropertyMatchBean[]) =>
                    propertiesData?.length > 0
                ),
                take(1),
                map((propertiesData: PropertyMatchBean[]) => ({
                  propertiesData,
                  applicationId,
                  intentApplication: propertiesData.find(
                    p =>
                      p.id.toString() ===
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                      confirmIntentForApplication?.toString()
                  ),
                  customQuestionProperty:
                    propertiesData.find(
                      this.findByIdWithCustomQuestions(applicationId)
                    ) || propertiesData.find(this.findByOpenCustomQuestions())
                })),
                tap(({ customQuestionProperty, intentApplication }) => {
                  if (intentApplication) {
                    this.store.dispatch(
                      OpenIntentModal({
                        application: intentApplication,
                        intent: 'INTENT'
                      })
                    );
                  } else if (
                    customQuestionProperty &&
                    customQuestionProperty.type ===
                      PropertySearcherType.APPLICATION
                  ) {
                    this.localStorageService.removeItem(
                      storageKeys.customQuestionApplicationId
                    );
                    this.store.dispatch(
                      new OpenCustomQuestionsModal(customQuestionProperty)
                    );
                  }
                })
              )
            )
          )
        )
      ),
    { dispatch: false }
  );

  loadRentedFlatsApplicationsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadRentedFlatsApplicationsSuccess),
      switchMap(({ applications }) => {
        if (applications.length === 0) return [];
        const ids =
          this.localStorage.getItem<string[]>(
            storageKeys.rentedPropertiesIds
          ) || [];
        const foundApplication = applications.find(
          application => !ids.includes(application.property.id)
        );

        const property = foundApplication?.property;

        if (!property) return [];

        this.localStorage.setItem(storageKeys.rentedPropertiesIds, [
          ...ids,
          property.id
        ]);

        const modal = this.modalService.open(TenantAcceptanceModalComponent, {
          data: {
            property: property
          },
          windowClass: 'new-home__modal-container'
        });

        this.rentedStore.loadRentedProperties();

        return race(
          modal.onClose().pipe(
            mergeMap(({ activateSearchProfile, closeOpenApplication }) => {
              const actions: Action[] = [];
              if (activateSearchProfile) {
                this.searchProfileStore.activateAllSearchProfiles(
                  property.type
                );
              }
              if (!closeOpenApplication) {
                actions.push(RevokeAllOpenApplications());
              }
              if (!this.route.snapshot.queryParams.fromMail) {
                this.modalService.open(TenantExperienceModalComponent, {
                  data: {
                    foundApplication: foundApplication,
                    rentDepositInfo: foundApplication.rentDepositInfo,
                    itpSettings: this.userStore.userItpSettings()
                  },
                  windowClass: 'new-home__modal-container'
                });
              }

              actions.push(new Go({ path: [MainPageNavigation.NEW_HOME] }));
              return actions;
            })
          ),
          modal.onDismiss().pipe(
            tap(() => {
              this.searchProfileStore.activateAllSearchProfiles(property.type);
            }),
            map(
              () =>
                new Go({
                  path: [MainPageNavigation.NEW_HOME]
                })
            )
          )
        );
      })
    )
  );

  private findByIdWithCustomQuestions(applicationId: string) {
    return (propertyMatch: PropertyMatchBean) => {
      return (
        propertyMatch.id.toString() === applicationId &&
        propertyMatch.hasQuestions &&
        (!!propertyMatch.questionContainer?.customQuestions?.length ||
          !!propertyMatch.questionContainer?.questions?.length)
      );
    };
  }

  private findByOpenCustomQuestions() {
    return (propertyMatch: PropertyMatchBean) => {
      return (
        propertyMatch.hasQuestions &&
        (propertyMatch.questionContainer?.customQuestions?.some(
          (c: CustomQuestion) => isValueNullOrUndefined(c.answer)
        ) ||
          propertyMatch.questionContainer?.questions.some(container => {
            const mainQuestionResponse = container.responses?.find(
              response =>
                response.questionId === container.rootQuestion.mainQuestionId
            );
            return (
              !mainQuestionResponse ||
              (!mainQuestionResponse?.data.response &&
                !mainQuestionResponse?.data.answerIds)
            );
          }))
      );
    };
  }
}
